import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { isTourSeen, markTourSeen } from "../../../lib/tour";

/**
 * Tanıtım adımları. Hedef bulunamazsa (ekranda yoksa) adım atlanır.
 * Sol menü ve tema düğmesi uygulama kabında; data-tour yoksa yedek seçici.
 */
const STEPS = [
  {
    selectors: ['[data-tour="nav"]', "aside nav", "aside"],
    title: "Menü",
    body: "Gruplarınız, yönettiğiniz gruplar ve hatimler, istatistik ve hesap ayarları buradan açılır.",
  },
  {
    selectors: ['[data-tour="month"]'],
    title: "Ayın özeti",
    body: "Ayı değiştirin; yıldızlar, en uzun zincir, ortalama ve kayıt girilen günler burada toplanır.",
  },
  {
    selectors: ['[data-tour="circles"]'],
    title: "Daireler",
    body: "Hatimleriniz, müzakereleriniz ve kaza namazlarınız burada. Bir daireye basıp girin; en baştaki Katıl ile hatime ya da gruba katılın.",
  },
  {
    selectors: ['[data-tour="days"]'],
    title: "Günler",
    body: "Bir güne tıklayın; o günün daireleri ve hedefleri açılır. Dolu gün tamamlanmış, açık renk kısmen yapılmış, boş gün kaçmış demek.",
  },
  {
    selectors: ['[data-tour="goals"]'],
    title: "Hedeflerim",
    body: "Artı ve eksi ile değer girin, Tamamla ile hedefi doldurun, sonra Kaydet'e basın. Kalemle hedef ekleyip düzenleyin.",
  },
  {
    selectors: ['[data-tour="groups"]'],
    title: "Gruplarım",
    body: "Üyesi olduğunuz gruplar. Bir gruba basınca bu haftaki sıranız, tamamlamanız ve grubun ayrıntıları açılır.",
  },
  {
    selectors: ['[data-tour="meter"]'],
    title: "Günün metresi",
    body: "Hedefler, hatim ve müzakere ilerlemeniz burada toplanır. Hepsi bitince çubuk yeşile döner.",
  },
  {
    selectors: [
      '[data-tour="theme-toggle"]',
      'aside button[aria-label="Koyu tema"]',
      'aside button[aria-label="Açık tema"]',
    ],
    title: "Koyu ve açık tema",
    body: "Bu düğmeyle koyu ve açık tema arasında geçin; seçiminiz hatırlanır.",
  },
];

const PAD = 8;
const GAP = 14;
/** Açıklama kartının yaklaşık yüksekliği (yerleşim hesabı için). */
const CARD_H = 200;

function findTarget(selectors) {
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

/** MUI pencereleri (Dialog, Drawer) açıkken tanıtım başlamaz. */
const modalOpen = () => Boolean(document.querySelector(".MuiModal-root:not(.MuiModal-hidden)"));

/**
 * İlk girişte ana sayfa tanıtımı (mobildeki tanıtımın web karşılığı): ekran
 * kararır, sıradaki alan açık kalır, yanında açıklama kartı çıkar. Geç ya da
 * Başlayalım denince kullanıcı başına bir daha gösterilmez.
 */
export default function WebTour({ ready }) {
  const { t } = useScriptMode();
  const { user } = useAuth();
  const userId = user?._id || user?.id || null;
  const [steps, setSteps] = useState(null);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [view, setView] = useState({ w: 0, h: 0 });

  // Sayfa yerleşince, pencere açık değilse, görülmediyse başlar.
  useEffect(() => {
    if (!ready || !userId || steps || isTourSeen(userId)) return undefined;
    const timer = setInterval(() => {
      if (modalOpen()) return;
      clearInterval(timer);
      const found = STEPS.filter((s) => findTarget(s.selectors));
      if (found.length) {
        setSteps(found);
        setIndex(0);
      }
    }, 700);
    return () => clearInterval(timer);
  }, [ready, userId, steps]);

  const measure = useCallback(() => {
    if (!steps) return;
    const el = findTarget(steps[index].selectors);
    setView({ w: window.innerWidth, h: window.innerHeight });
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: "nearest", inline: "nearest" });
    const r = el.getBoundingClientRect();
    // Çerçeve ekran kenarından taşmasın; boydan boya alanlarda da görünür kalsın.
    const x = Math.max(2, r.left - PAD);
    const y = Math.max(2, r.top - PAD);
    const x2 = Math.min(window.innerWidth - 2, r.right + PAD);
    const y2 = Math.min(window.innerHeight - 2, r.bottom + PAD);
    setRect({ x, y, w: x2 - x, h: y2 - y });
  }, [steps, index]);

  useLayoutEffect(() => {
    measure();
    if (!steps) return undefined;
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [measure, steps]);

  // Esc ile tanıtım kapanır (Geç ile aynı).
  useEffect(() => {
    if (!steps) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      markTourSeen(userId);
      setSteps(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [steps, userId]);

  if (!steps) return null;

  const step = steps[index];
  const last = index === steps.length - 1;
  const finish = () => {
    markTourSeen(userId);
    setSteps(null);
  };

  // Kart: sığdığı ilk yere (alt, üst, sağ, sol) konur; hiçbir yere sığmazsa
  // ekranın ortasına. Sol menü gibi boydan boya alanlarda kart yanına düşer.
  const cardWidth = Math.min(380, view.w - 32);
  const clampX = (x) => Math.min(Math.max(16, x), view.w - cardWidth - 16);
  const clampY = (y) => Math.min(Math.max(16, y), view.h - CARD_H - 16);
  let cardPos = { left: (view.w - cardWidth) / 2, top: clampY(view.h / 2 - CARD_H / 2) };
  if (rect) {
    const bottom = rect.y + rect.h;
    const right = rect.x + rect.w;
    const midX = clampX(rect.x + rect.w / 2 - cardWidth / 2);
    const midY = clampY(rect.y + rect.h / 2 - CARD_H / 2);
    if (view.h - bottom >= CARD_H + GAP + 16) cardPos = { left: midX, top: bottom + GAP };
    else if (rect.y >= CARD_H + GAP + 16) cardPos = { left: midX, bottom: view.h - rect.y + GAP };
    else if (view.w - right >= cardWidth + GAP + 16) cardPos = { left: right + GAP, top: midY };
    else if (rect.x >= cardWidth + GAP + 16) cardPos = { left: rect.x - GAP - cardWidth, top: midY };
  }

  return createPortal(
    <div className="fixed inset-0 z-[2000]" role="dialog" aria-modal="true" aria-label={t(step.title)}>
      <svg width={view.w} height={view.h} className="absolute inset-0" aria-hidden>
        <defs>
          <mask id="web-tour-hole">
            <rect x="0" y="0" width={view.w} height={view.h} fill="white" />
            {rect ? (
              <rect
                x={rect.x}
                y={rect.y}
                width={rect.w}
                height={rect.h}
                rx="16"
                ry="16"
                fill="black"
                style={{ transition: "all 250ms ease" }}
              />
            ) : null}
          </mask>
        </defs>
        <rect x="0" y="0" width={view.w} height={view.h} fill="rgba(4,22,24,0.74)" mask="url(#web-tour-hole)" />
      </svg>
      {rect ? (
        <div
          className="absolute rounded-2xl border-2 border-white/85 pointer-events-none"
          style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, transition: "all 250ms ease" }}
        />
      ) : null}

      <div
        className="absolute rounded-2xl p-4 flex flex-col gap-2 bg-white text-terakkiInk shadow-xl dark:bg-[#123B3D] dark:text-white dark:border dark:border-white/15"
        style={{ width: cardWidth, ...cardPos }}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-md font-SemiBold">{t(step.title)}</span>
          <span className="text-sm text-terakkiNeutral dark:text-white/70">
            {index + 1} / {steps.length}
          </span>
        </div>
        <p className="text-base leading-5">{t(step.body)}</p>
        <div className="flex items-center justify-between mt-1">
          {!last ? (
            <button
              type="button"
              onClick={finish}
              className="text-base text-terakkiNeutral dark:text-white/70 px-1 py-2"
            >
              {t("Geç")}
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {index > 0 ? (
              <button
                type="button"
                onClick={() => setIndex((i) => i - 1)}
                className="h-10 px-4 rounded-full text-base font-SemiBold bg-terakkiBrandCloud text-terakkiPrimaryDark dark:bg-white/10 dark:text-white"
              >
                {t("Geri")}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => (last ? finish() : setIndex((i) => i + 1))}
              className={`h-10 px-4 rounded-full text-base font-SemiBold ${
                last
                  ? "bg-[#2E9E6A] text-white dark:bg-[#34B37A]"
                  : "bg-terakkiPrimaryDark text-white dark:bg-white dark:text-terakkiPrimaryDark"
              }`}
            >
              {last ? t("Başlayalım") : t("İleri")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
